import axios from "axios";
import { toast } from "react-toastify";
import { API_TOKEN, API_URL } from "../../Config/secrets";

// Organization Management > Fair Organizations and the fair detail page (admin backend
// /fair-management). No slice state: the pages keep their own rows. Each thunk resolves with the
// response's `data`, or null after showing the error.

const header = {
  Authorization: API_TOKEN,
};

const errorMessage = (error, fallback) => {
  const message = error?.response?.data?.message || error?.message || fallback;
  return Array.isArray(message) ? message.join(", ") : message;
};

// { skip (page), take, query } -> { data: [fair orgs], count }
export function getFairOrganizations(page = 1, take = 10, query = "") {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/get-fair-organizations`,
        { skip: page, take, query },
        { headers: header }
      );
      return response?.data?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to fetch fair organizations"), { autoClose: 3000 });
      return null;
    }
  };
}

export function getFairProfile(organizationId) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/get-fair-profile`,
        { organization_id: Number(organizationId) },
        { headers: header }
      );
      return response?.data?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to fetch the fair"), { autoClose: 3000 });
      return null;
    }
  };
}

// changes = any of name, website, countries, founded_year, primary_audience, categories,
// facebook, instagram, linkedin
export function updateFairProfile(organizationId, changes) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/update-fair-profile`,
        { organization_id: Number(organizationId), ...changes },
        { headers: header }
      );
      toast.success(response?.data?.message || "Fair updated", { autoClose: 3000 });
      return response?.data?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to update the fair"), { autoClose: 3000 });
      return null;
    }
  };
}

export function getFairSeasons(organizationId) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/get-fair-seasons`,
        { organization_id: Number(organizationId) },
        { headers: header }
      );
      return response?.data?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to fetch seasons"), { autoClose: 3000 });
      return null;
    }
  };
}

// season = { id?, season_name, description, categories, start_date, end_date }
export function saveFairSeason(organizationId, season) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/save-fair-season`,
        { organization_id: Number(organizationId), ...season },
        { headers: header }
      );
      toast.success(response?.data?.message || "Season saved", { autoClose: 3000 });
      return response?.data?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save the season"), { autoClose: 3000 });
      return null;
    }
  };
}

// params = { fair_id, type, re_booked, skip (page), take, query } -> { items, total, tabs }
export function getSeasonExhibitors(organizationId, params) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/get-season-exhibitors`,
        { organization_id: Number(organizationId), ...params },
        { headers: header }
      );
      return response?.data?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to fetch exhibitors"), { autoClose: 3000 });
      return null;
    }
  };
}

// -> { message, data: { summary, invalid_records } }: the message is shown by the caller
export function upsertSeasonExhibitors(organizationId, fairId, rows) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/upsert-season-exhibitors`,
        { organization_id: Number(organizationId), fair_id: fairId, fair_exhibitors: rows },
        { headers: header }
      );
      return response?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to add exhibitors"), { autoClose: 3000 });
      return null;
    }
  };
}

export function deleteSeasonExhibitors(organizationId, exhibitorIds) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/delete-season-exhibitors`,
        { organization_id: Number(organizationId), exhibitor_ids: exhibitorIds },
        { headers: header }
      );
      toast.success(response?.data?.message || "Exhibitors removed", { autoClose: 3000 });
      return response?.data?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to remove exhibitors"), { autoClose: 3000 });
      return null;
    }
  };
}

// The fair's "Request To Admin" rows: [{ id, status, created_at, files, requested_by }]
export function getFairAdminRequests(organizationId) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/get-fair-admin-requests`,
        { organization_id: Number(organizationId) },
        { headers: header }
      );
      return response?.data?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to fetch data requests"), { autoClose: 3000 });
      return null;
    }
  };
}

// status = 'pending' | 'completed' (the fair isn't notified)
export function updateFairAdminRequest(organizationId, id, status) {
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/fair-management/update-fair-admin-request`,
        { organization_id: Number(organizationId), id, status },
        { headers: header }
      );
      toast.success(response?.data?.message || "Request updated", { autoClose: 3000 });
      return response?.data?.data;
    } catch (error) {
      toast.error(errorMessage(error, "Failed to update the request"), { autoClose: 3000 });
      return null;
    }
  };
}
